// Mercapoli - Admin DIGGII Management Page
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Coins, Users, TrendingUp, DollarSign, RefreshCw,
  ArrowLeft, Loader2, Search, Plus, Edit2, Save, X,
  BarChart3, Wallet, Globe
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { diggii as diggiiApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";

const AdminDiggii = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [resumen, setResumen] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [tiposCambio, setTiposCambio] = useState([]);
  const [activeTab, setActiveTab] = useState('resumen');
  
  // Modales
  const [showAjusteModal, setShowAjusteModal] = useState(false);
  const [showTasaModal, setShowTasaModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTasa, setSelectedTasa] = useState(null);
  
  // Form states
  const [ajusteForm, setAjusteForm] = useState({
    cantidad: '',
    tipo: 'promocion',
    descripcion: ''
  });
  const [nuevaTasa, setNuevaTasa] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [resumenData, usuariosData, tasasData] = await Promise.all([
        diggiiApi.getResumenAdmin(),
        diggiiApi.getUsuariosAdmin({ limit: 50 }),
        diggiiApi.getTiposCambio()
      ]);
      setResumen(resumenData);
      setUsuarios(usuariosData);
      setTiposCambio(tasasData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error cargando datos DIGGII');
    }
    setLoading(false);
  };

  const handleAjuste = async () => {
    if (!selectedUser || !ajusteForm.cantidad || !ajusteForm.descripcion) {
      toast.error('Completa todos los campos');
      return;
    }

    try {
      await diggiiApi.crearAjuste({
        user_id: selectedUser.user_id,
        cantidad: parseFloat(ajusteForm.cantidad),
        tipo: ajusteForm.tipo,
        descripcion: ajusteForm.descripcion
      });
      toast.success('Ajuste aplicado correctamente');
      setShowAjusteModal(false);
      setAjusteForm({ cantidad: '', tipo: 'promocion', descripcion: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error aplicando ajuste');
    }
  };

  const handleUpdateTasa = async () => {
    if (!selectedTasa || !nuevaTasa) {
      toast.error('Ingresa una tasa válida');
      return;
    }

    try {
      await diggiiApi.updateTipoCambio(selectedTasa.pais, parseFloat(nuevaTasa));
      toast.success(`Tasa de ${selectedTasa.pais} actualizada`);
      setShowTasaModal(false);
      setNuevaTasa('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando tasa');
    }
  };

  const openAjusteModal = (usuario) => {
    setSelectedUser(usuario);
    setShowAjusteModal(true);
  };

  const openTasaModal = (tasa) => {
    setSelectedTasa(tasa);
    setNuevaTasa(tasa.tasa_usd.toString());
    setShowTasaModal(true);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="p-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="Mercapoli" className="w-8 h-8" />
                <div>
                  <h1 className="font-heading font-bold">Sistema DIGGII</h1>
                  <p className="text-xs text-muted-foreground">Programa de Fidelidad</p>
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-6xl">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Card className="bg-gradient-to-br from-amber-500 to-yellow-500 text-white">
                  <CardContent className="p-4">
                    <Coins className="h-8 w-8 mb-2 opacity-80" />
                    <p className="text-sm opacity-80">DIGGII Circulante</p>
                    <p className="text-2xl font-bold">{resumen?.total_diggii_circulante?.toFixed(2) || 0}</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <Card>
                  <CardContent className="p-4">
                    <DollarSign className="h-8 w-8 mb-2 text-green-500" />
                    <p className="text-sm text-muted-foreground">Valor en USD</p>
                    <p className="text-2xl font-bold text-green-600">${resumen?.valor_circulante_usd?.toFixed(2) || 0}</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card>
                  <CardContent className="p-4">
                    <Users className="h-8 w-8 mb-2 text-blue-500" />
                    <p className="text-sm text-muted-foreground">Usuarios con DIGGII</p>
                    <p className="text-2xl font-bold">{resumen?.total_usuarios_con_diggii || 0}</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <Card>
                  <CardContent className="p-4">
                    <TrendingUp className="h-8 w-8 mb-2 text-purple-500" />
                    <p className="text-sm text-muted-foreground">DIGGII Canjeados</p>
                    <p className="text-2xl font-bold">{resumen?.total_diggii_canjeado?.toFixed(2) || 0}</p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Config info */}
            <Card className="border-amber-500/30 bg-amber-50 dark:bg-amber-950/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Coins className="h-6 w-6 text-amber-500" />
                  <div>
                    <p className="font-medium">Configuración DIGGII</p>
                    <p className="text-sm text-muted-foreground">
                      Por cada <span className="font-bold text-amber-600">${resumen?.config?.ratio_acumulacion_usd || 200} USD</span> gastados = 
                      <span className="font-bold text-amber-600"> 1 DIGGII</span> • 
                      1 DIGGII = <span className="font-bold text-green-600">${resumen?.config?.valor_diggii_usd || 1} USD</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="usuarios" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Usuarios
                </TabsTrigger>
                <TabsTrigger value="tasas" className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Tasas de Cambio
                </TabsTrigger>
              </TabsList>

              {/* Tab: Usuarios */}
              <TabsContent value="usuarios" className="space-y-4">
                {usuarios.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <Coins className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="text-muted-foreground">No hay usuarios con DIGGII</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {usuarios.map((cuenta) => (
                      <Card key={cuenta.user_id}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                <span className="font-bold text-amber-600">
                                  {cuenta.usuario?.nombre?.charAt(0) || '?'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{cuenta.usuario?.nombre || 'Usuario'}</p>
                                <p className="text-sm text-muted-foreground">{cuenta.usuario?.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-lg font-bold text-amber-600">{cuenta.balance.toFixed(2)} DIGGII</p>
                                <p className="text-xs text-muted-foreground">
                                  Gastado: ${cuenta.total_gastado_usd.toFixed(2)} USD
                                </p>
                              </div>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openAjusteModal(cuenta)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Tab: Tasas de Cambio */}
              <TabsContent value="tasas" className="space-y-4">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tiposCambio.map((tasa) => (
                    <Card key={tasa.pais} className={!tasa.activo ? 'opacity-50' : ''}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{tasa.pais}</Badge>
                            <span className="font-medium">{tasa.moneda}</span>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openTasaModal(tasa)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-2xl font-bold">
                          {tasa.simbolo}{tasa.tasa_usd.toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          1 USD = {tasa.simbolo}{tasa.tasa_usd.toFixed(2)} {tasa.moneda}
                        </p>
                        {tasa.updated_at && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Actualizado: {new Date(tasa.updated_at).toLocaleDateString()}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </main>

      {/* Modal: Ajuste DIGGII */}
      <Dialog open={showAjusteModal} onOpenChange={setShowAjusteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar DIGGII</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted p-3 rounded-lg">
              <p className="font-medium">{selectedUser?.usuario?.nombre}</p>
              <p className="text-sm text-muted-foreground">{selectedUser?.usuario?.email}</p>
              <p className="text-sm">Balance actual: <span className="font-bold text-amber-600">{selectedUser?.balance?.toFixed(2)} DIGGII</span></p>
            </div>

            <div className="space-y-2">
              <Label>Cantidad (positivo para agregar, negativo para quitar)</Label>
              <Input
                type="number"
                step="0.01"
                value={ajusteForm.cantidad}
                onChange={(e) => setAjusteForm({...ajusteForm, cantidad: e.target.value})}
                placeholder="Ej: 5.00 o -2.50"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo de ajuste</Label>
              <Select
                value={ajusteForm.tipo}
                onValueChange={(v) => setAjusteForm({...ajusteForm, tipo: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="promocion">Promoción</SelectItem>
                  <SelectItem value="ajuste">Ajuste/Corrección</SelectItem>
                  <SelectItem value="bono">Bono especial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={ajusteForm.descripcion}
                onChange={(e) => setAjusteForm({...ajusteForm, descripcion: e.target.value})}
                placeholder="Motivo del ajuste..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAjusteModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAjuste} className="bg-amber-500 hover:bg-amber-600">
              Aplicar Ajuste
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Editar Tasa */}
      <Dialog open={showTasaModal} onOpenChange={setShowTasaModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Tasa de Cambio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted p-3 rounded-lg">
              <p className="font-medium">{selectedTasa?.pais} - {selectedTasa?.moneda}</p>
              <p className="text-sm text-muted-foreground">
                Tasa actual: {selectedTasa?.simbolo}{selectedTasa?.tasa_usd?.toFixed(2)}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Nueva tasa (cuántas {selectedTasa?.moneda} = 1 USD)</Label>
              <Input
                type="number"
                step="0.01"
                value={nuevaTasa}
                onChange={(e) => setNuevaTasa(e.target.value)}
                placeholder="Ej: 7.80"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTasaModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateTasa}>
              <Save className="h-4 w-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDiggii;
